import * as React from 'react';
import { ActionButton, Icon, MessageBar, MessageBarType, Toggle } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IPlanPreview, PreviewMark } from '../../../models/Plan';

export interface IBeforeAfterViewProps {
  libraryName: string;
  preview: IPlanPreview;
  includePending: boolean;
  onIncludePendingChange: (value: boolean) => void;
}

interface INode {
  name: string;
  path: string;
  kind: 'file' | 'folder';
  mark?: PreviewMark;
  /** A descendant (or this node) is marked; used to auto-expand. */
  hasMark: boolean;
  children: INode[];
}

const MARK_LABEL: { [mark in PreviewMark]: string } = {
  moved: 'Moved',
  renamed: 'Renamed',
  quarantined: 'Quarantined',
  merged: 'Consolidated'
};

const MARK_CLASS: { [mark in PreviewMark]: string } = {
  moved: styles.markMoved,
  renamed: styles.markRenamed,
  quarantined: styles.markQuarantined,
  merged: styles.markMerged
};

function buildTree(items: { path: string; kind: 'file' | 'folder'; mark?: PreviewMark }[]): INode {
  const root: INode = { name: '', path: '', kind: 'folder', hasMark: false, children: [] };
  const index: { [path: string]: INode } = { '': root };

  const ensureFolder = (path: string): INode => {
    const key: string = path.toLowerCase();
    if (index[key]) {
      return index[key];
    }
    const slash: number = path.lastIndexOf('/');
    const parent: INode = ensureFolder(slash === -1 ? '' : path.substring(0, slash));
    const node: INode = { name: path.substring(slash + 1), path, kind: 'folder', hasMark: false, children: [] };
    parent.children.push(node);
    index[key] = node;
    return node;
  };

  for (const item of items) {
    let node: INode;
    if (item.kind === 'folder') {
      node = ensureFolder(item.path);
    } else {
      const slash: number = item.path.lastIndexOf('/');
      const parent: INode = ensureFolder(slash === -1 ? '' : item.path.substring(0, slash));
      node = { name: item.path.substring(slash + 1), path: item.path, kind: 'file', hasMark: false, children: [] };
      parent.children.push(node);
    }
    if (item.mark) {
      node.mark = item.mark;
    }
  }

  const finish = (node: INode): boolean => {
    node.children.sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'folder' ? -1 : 1));
    let marked: boolean = !!node.mark;
    for (const child of node.children) {
      marked = finish(child) || marked;
    }
    node.hasMark = marked;
    return marked;
  };
  finish(root);
  return root;
}

function collectFolders(node: INode, out: { [path: string]: boolean }, onlyMarked: boolean): void {
  for (const child of node.children) {
    if (child.kind === 'folder' && (!onlyMarked || child.hasMark)) {
      out[child.path] = true;
      collectFolders(child, out, onlyMarked);
    }
  }
}

function TreeNodes(props: { node: INode; depth: number; expanded: { [path: string]: boolean }; onToggle: (path: string) => void }): React.ReactElement {
  const { node, depth, expanded, onToggle } = props;
  return (
    <>
      {node.children.map(child => {
        const isOpen: boolean = !!expanded[child.path];
        const isFolder: boolean = child.kind === 'folder';
        return (
          <React.Fragment key={child.path}>
            <li className={styles.treeRow} style={{ paddingLeft: depth * 18 + 4 }}>
              <button
                type="button"
                className={styles.treeToggle}
                onClick={() => isFolder && onToggle(child.path)}
                disabled={!isFolder || child.children.length === 0}
                aria-expanded={isFolder && child.children.length > 0 ? isOpen : undefined}
              >
                <Icon iconName={isFolder && child.children.length > 0 ? (isOpen ? 'ChevronDown' : 'ChevronRight') : ''} className={styles.treeChevron} />
                <Icon iconName={isFolder ? 'FabricFolder' : 'Page'} className={styles.treeIcon} />
                <span className={styles.treeName}>{child.name}</span>
              </button>
              {child.mark && <span className={`${styles.mark} ${MARK_CLASS[child.mark]}`}>{MARK_LABEL[child.mark]}</span>}
            </li>
            {isFolder && isOpen && <TreeNodes node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} />}
          </React.Fragment>
        );
      })}
    </>
  );
}

function PathTree(props: { title: string; root: INode; libraryName: string }): React.ReactElement {
  const { title, root, libraryName } = props;
  const [expanded, setExpanded] = React.useState<{ [path: string]: boolean }>({});

  // Open the branches that contain changes whenever the tree changes.
  React.useEffect(() => {
    const open: { [path: string]: boolean } = {};
    collectFolders(root, open, true);
    setExpanded(open);
  }, [root]);

  const expandAll = (): void => {
    const open: { [path: string]: boolean } = {};
    collectFolders(root, open, false);
    setExpanded(open);
  };

  return (
    <div className={styles.compareColumn}>
      <div className={styles.compareHeader}>
        <h4 className={styles.compareTitle}>{title}</h4>
        <ActionButton iconProps={{ iconName: 'ExploreContent' }} text="Expand all" onClick={expandAll} />
        <ActionButton iconProps={{ iconName: 'CollapseContent' }} text="Collapse" onClick={() => setExpanded({})} />
      </div>
      <ul className={styles.tree}>
        <li className={styles.treeRow}>
          <Icon iconName="Library" className={styles.treeIcon} />
          <strong className={styles.treeName}>{libraryName}</strong>
        </li>
        <TreeNodes node={root} depth={1} expanded={expanded} onToggle={p => setExpanded(prev => ({ ...prev, [p]: !prev[p] }))} />
      </ul>
    </div>
  );
}

/** Side-by-side current and proposed library structure. */
export default function BeforeAfterView(props: IBeforeAfterViewProps): React.ReactElement {
  const { libraryName, preview, includePending, onIncludePendingChange } = props;

  const before: INode = React.useMemo(
    () => buildTree(preview.entries.map(e => ({ path: e.beforePath, kind: e.kind, mark: e.mark }))),
    [preview]
  );
  const after: INode = React.useMemo(
    () => buildTree(preview.entries.map(e => ({ path: e.afterPath, kind: e.kind, mark: e.mark }))),
    [preview]
  );
  const changed: number = preview.entries.filter(e => e.mark).length;

  return (
    <div>
      <div className={styles.compareToolbar}>
        <Toggle
          label="Include changes not yet approved"
          inlineLabel
          checked={includePending}
          onChange={(_, checked) => onIncludePendingChange(!!checked)}
        />
        <div className={styles.legend}>
          {(Object.keys(MARK_LABEL) as PreviewMark[]).map(m => (
            <span key={m} className={`${styles.mark} ${MARK_CLASS[m]}`}>{MARK_LABEL[m]}</span>
          ))}
        </div>
      </div>

      {changed === 0 && (
        <MessageBar>
          {includePending ? 'The plan has no changes to preview.' : 'No changes approved yet. Approve changes in the Organization plan tab, or include changes not yet approved.'}
        </MessageBar>
      )}
      {preview.conflicts.map(c => (
        <MessageBar key={c.message} messageBarType={MessageBarType.warning} isMultiline>{c.message}</MessageBar>
      ))}

      <div className={styles.compare}>
        <PathTree title="Current" root={before} libraryName={libraryName} />
        <PathTree title="Proposed" root={after} libraryName={libraryName} />
      </div>
    </div>
  );
}
